<script setup>
// O interruptor do bot no cabeçalho, em dois modos (PLAN §3.4):
//
//   bridge  inbox com agent bot de webhook, sem PowerAgent. É o toggle de antes, intocado:
//           grava `workforce_bot_active` com update otimista e o listener lê.
//   native  inbox com PowerAgent. O estado é o ESPELHO `additional_attributes.bot`, que só o
//           runtime escreve; o clique é `PUT .../bot` e a tela aplica a resposta — nunca o
//           que pediu. Um update otimista aqui seria um segundo escritor do espelho.
//   none    inbox sem bot nenhum: o cabeçalho não monta o botão. Um botão que grava uma
//           chave que ninguém lê é o defeito que este modo existe para tirar da tela.
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAgentBot } from 'dashboard/composables/useAgentBot';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  mode: {
    type: String,
    default: 'bridge',
    validator: value => ['native', 'bridge', 'none'].includes(value),
  },
});

const { t } = useI18n();
const store = useStore();
const { definirAtivo, causaDaPausa } = useAgentBot();

const currentChat = computed(() => store.getters.getSelectedChat);
const attrs = computed(() => currentChat.value?.additional_attributes || {});
const nativo = computed(() => props.mode === 'native');
const salvando = ref(false);

// `!== false` nos dois modos: espelho ausente (o runtime ainda não escreveu) é bot ativo,
// que é o estado de toda conversa que ninguém pausou.
const botActive = computed(() =>
  nativo.value
    ? attrs.value.bot?.active !== false
    : attrs.value.workforce_bot_active !== false
);

const label = computed(() => {
  if (nativo.value) {
    return botActive.value
      ? t('CONVERSATION.AGENT_BOT.ACTIVE')
      : t('CONVERSATION.AGENT_BOT.PAUSED');
  }
  return botActive.value
    ? t('WORKFORCE.BOT_ACTIVE')
    : t('WORKFORCE.BOT_PAUSED');
});

// No modo nativo o tooltip do bot pausado diz POR QUÊ: é a pergunta que quem chega na
// conversa faz antes de decidir se religa.
const tooltip = computed(() => {
  if (!nativo.value) {
    return botActive.value
      ? t('WORKFORCE.PAUSE_BOT')
      : t('WORKFORCE.RESUME_BOT');
  }
  if (botActive.value) return t('CONVERSATION.AGENT_BOT.PAUSE');
  return `${t('CONVERSATION.AGENT_BOT.RESUME')} · ${causaDaPausa(attrs.value.bot?.silenciado_por)}`;
});

const toggleBot = async () => {
  if (salvando.value) return;
  if (!nativo.value) {
    try {
      await store.dispatch('toggleWorkforceBot', {
        conversationId: props.conversationId,
      });
    } catch (error) {
      // handled by store
    }
    return;
  }
  salvando.value = true;
  try {
    await definirAtivo(props.conversationId, !botActive.value);
  } catch (error) {
    // já avisado pelo composable; o espelho não mudou, e o botão mostra isso
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <!-- teal e não green: o namespace n- do tema não tem green, e a classe morta deixava o
       botão "ativo" sem cor nenhuma (o computado saía transparente, sem erro). -->
  <button
    type="button"
    class="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer"
    :class="[
      botActive
        ? 'bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4'
        : 'bg-n-amber-3 text-n-amber-11 hover:bg-n-amber-4',
      salvando ? 'opacity-60 cursor-wait' : '',
    ]"
    :title="tooltip"
    :disabled="salvando"
    @click="toggleBot"
  >
    <fluent-icon
      :icon="botActive ? 'bot' : 'bot-sparkle'"
      size="14"
      class="flex-shrink-0"
    />
    <span class="whitespace-nowrap">{{ label }}</span>
  </button>
</template>
